import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { AnimatedCounter } from "@/components/AnimatedCounter";
import { WHY_POINTS, STATS, IMAGES, TRUST_ICON } from "@/data/content";
import { ArrowRight, Check } from "lucide-react";

const Label = ({ children, className = "" }) => (
    <span className={`font-mono text-[11px] uppercase tracking-[0.24em] ${className}`}>
        {children}
    </span>
);

export default function About() {
    return (
        <div data-testid="about-page" className="pt-[76px]">
            <Seo
                title="Why DentAssists — Dental-Only Billing Specialists in Texas"
                description="DentAssists is a dental-only billing and revenue cycle team serving practices across Texas and the U.S. We know CDT codes, dental payer rules, and how to lower AR. HIPAA-compliant with a signed BAA."
                path="/about"
            />
            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20 pb-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
                <Reveal>
                    <Label className="text-terracotta">Why DentAssists</Label>
                    <h1 className="font-heading text-5xl sm:text-6xl tracking-tight text-ink mt-6 leading-[1]">
                        We speak <span className="italic text-sage">dental</span>, fluently.
                    </h1>
                    <p className="text-base lg:text-lg text-inksoft leading-relaxed mt-7 max-w-xl">
                        We work exclusively with dental practices, so our team already knows CDT
                        codes, dental payer quirks, and the difference between a missing frequency
                        limitation and a real denial. No ramp-up, no generalist guesswork.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="relative">
                        <div className="absolute -bottom-4 -right-4 w-full h-full border border-linec rounded-sm hidden lg:block" />
                        <img
                            src={IMAGES.trust}
                            alt="Dental professional"
                            className="relative w-full h-[420px] object-cover rounded-sm"
                        />
                    </div>
                </Reveal>
            </section>

            <section className="bg-sage text-cream relative z-10">
                <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-16">
                    <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-cream/15">
                        {STATS.map((s, i) => (
                            <Reveal key={s.label} delay={i * 0.08}>
                                <div className="border-r border-b border-cream/15 p-8">
                                    <div className="font-heading text-4xl lg:text-5xl text-cream">
                                        <AnimatedCounter value={s.value} />
                                    </div>
                                    <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60 mt-3">
                                        {s.label}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-14">
                <Reveal>
                    <Label className="text-terracotta">Our approach</Label>
                    <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink mt-4">
                        A billing partner that works like part of your team.
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="space-y-5 text-base text-inksoft leading-relaxed">
                        <p>
                            DentAssists was built around a simple idea: dental practices lose real
                            money to insurance friction, and most of that loss is preventable. Claims
                            get denied for missing attachments, benefits aren't verified before the
                            appointment, and aging reports quietly grow while the front desk juggles
                            patients. We take that entire workload off your plate.
                        </p>
                        <p>
                            Because we work exclusively in dental, there is no ramp-up on CDT coding,
                            frequency limitations, downgrades, or the specific rules of the payers you
                            deal with every day. Our specialists log into the practice management
                            system you already use — Dentrix, Eaglesoft, Open Dental, and others — so
                            your team doesn't change a single workflow or learn new software.
                        </p>
                        <p>
                            Every engagement begins with a practice assessment and a signed Business
                            Associate Agreement, then a fast, structured onboarding that gets us
                            working claims inside your system within days. From there you get clean
                            claims submitted quickly, payments posted accurately, aging worked instead
                            of ignored, and plain-language reporting so you always know where your
                            revenue stands.
                        </p>
                    </div>
                </Reveal>
            </section>

            <section className="max-w-[1240px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-2 gap-14">
                <Reveal>
                    <Label className="text-terracotta">What sets us apart</Label>
                    <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink mt-4">
                        Built around how dental practices actually run.
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <ul className="space-y-6">
                        {WHY_POINTS.map((p) => (
                            <li key={p} className="flex items-start gap-4 border-b border-linec pb-6">
                                <Check size={20} className="text-terracotta shrink-0 mt-1" />
                                <span className="text-base text-inksoft leading-relaxed">{p}</span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </section>

            <section className="bg-stone2/50 border-y border-linec">
                <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 text-center">
                    <Reveal>
                        <TRUST_ICON size={40} className="text-terracotta mx-auto" strokeWidth={1.5} />
                        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-ink mt-6 max-w-2xl mx-auto">
                            HIPAA-compliant with a signed BAA before we ever touch a claim.
                        </h2>
                        <p className="text-inksoft mt-5 max-w-xl mx-auto leading-relaxed">
                            Every engagement starts with a Business Associate Agreement and secure,
                            access-controlled workflows. Your patient and claims data stays protected.
                        </p>
                        <Link
                            to="/contact"
                            data-testid="about-cta-button"
                            className="inline-flex items-center gap-2 bg-terracotta text-cream px-8 py-4 rounded-sm font-medium mt-9 transition-[background-color,transform] duration-200 hover:bg-terracottadark hover:-translate-y-0.5"
                        >
                            Get a Free Consultation
                            <ArrowRight size={18} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
